import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function CreateTestModal({ onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    title: '',
    instructions: '',
    duration_minutes: 30,
    questions: []
  });
  const [currentQuestion, setCurrentQuestion] = useState({
    question_text: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.questions.length === 0) {
      alert('Please add at least one question');
      return;
    }
    // Validate instructions have content
    const instructionsContent = formData.instructions.replace(/<[^>]*>/g, '').trim();
    if (!instructionsContent) {
      alert('Please enter instructions');
      return;
    }
    onSubmit(formData);
  };

  const addQuestion = () => {
    // Strip HTML tags and check if there's actual content
    const textContent = currentQuestion.question_text.replace(/<[^>]*>/g, '').trim();
    if (!textContent) {
      alert('Please enter a question');
      return;
    }
    setFormData({
      ...formData,
      questions: [...formData.questions, { ...currentQuestion }]
    });
    setCurrentQuestion({ question_text: '' });
  };

  const removeQuestion = (index) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <span className="text-3xl">📝</span>
            Create Test
          </h2>
          <p className="text-gray-600">Set up a new video-based test</p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Test Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
              placeholder="e.g., Math Assessment - Level 1"
              required
            />
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Duration (minutes) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.duration_minutes}
              onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
              className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
              min="1"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: 30-45 minutes</p>
          </div>

          <div className="mb-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Instructions <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-gray-300 rounded-lg focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-200 transition-all">
              <ReactQuill
                theme="snow"
                value={formData.instructions}
                onChange={(value) => setFormData({ ...formData, instructions: value })}
                placeholder="Provide clear instructions for candidates. Use formatting options to make it clear and readable."
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'indent': '-1'}, { 'indent': '+1' }],
                    ['link'],
                    ['clean']
                  ]
                }}
                style={{ minHeight: '200px' }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">These instructions will be shown to candidates before they start the test. Use the toolbar to format text.</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Questions <span className="text-red-500">*</span>
              <span className="text-xs font-normal text-gray-500 ml-2">
                ({formData.questions.length} added)
              </span>
            </label>
            
            <div className="mb-3 p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
              <div className="border-2 border-gray-300 rounded-lg mb-3 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-200 transition-all">
                <ReactQuill
                  theme="snow"
                  value={currentQuestion.question_text}
                  onChange={(value) => setCurrentQuestion({ ...currentQuestion, question_text: value })}
                  placeholder="Enter question text. Use formatting options to make it clear and readable."
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'indent': '-1'}, { 'indent': '+1' }],
                      ['link'],
                      ['clean']
                    ]
                  }}
                  style={{ minHeight: '150px' }}
                />
              </div>
              <button
                type="button"
                onClick={addQuestion}
                className="w-full bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 font-semibold transition-all shadow-md hover:shadow-lg"
              >
                ➕ Add Question
              </button>
            </div>

            {formData.questions.length > 0 && (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData.questions.map((q, index) => (
                  <div key={index} className="flex justify-between items-start p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <div className="flex-1">
                      <span className="text-xs font-semibold text-indigo-700">Question {index + 1}:</span>
                      <div className="text-sm text-gray-700 mt-1" dangerouslySetInnerHTML={{ __html: q.question_text }} />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeQuestion(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-semibold ml-3 px-2 py-1 hover:bg-red-50 rounded"
                    >
                      ✕ Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-semibold transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 font-semibold transition-all shadow-lg hover:shadow-xl"
            >
              ✅ Create Test
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

